import { Button, Dialog, IconButton, Switch } from '@mui/material';
import { AvailableRelationGroupFilters, FilterKeys, GetGroupsQueryFilters } from '@schooly/api';
import { FilterRow, SubjectSelectMultiple } from '@schooly/components/filters';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';

type AddGroupsForRelationFiltersPopupProps = {
  filters: AvailableRelationGroupFilters;
  defaultFilters: AvailableRelationGroupFilters;
  onClose: () => void;
  onSetFilters: (v: AvailableRelationGroupFilters) => void;
};

export const AddGroupsForRelationFiltersPopup: FC<AddGroupsForRelationFiltersPopupProps> = ({
  onClose,
  onSetFilters,
  filters: actualFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();

  const { schoolId } = useSchool();

  const [filters, setFilters] = useState<GetGroupsQueryFilters>(actualFilters);

  const handleApply = useCallback(() => {
    onSetFilters(filters);
    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const { onSelectSubject, onClearSubject, onClearOnlyTutorGroups, onSelectOnlyTutorGroups } =
    useMemo(() => {
      const clearFilter = (key: keyof typeof actualFilters) => () => {
        setFilters((filters) => ({
          ...filters,
          [key]: [],
        }));
      };

      return {
        onSelectSubject: (id: string) => {
          setFilters((filters) => ({
            ...filters,
            [FilterKeys.OnlyTutorGroups]: undefined,
            [FilterKeys.Subject]: filters[FilterKeys.Subject]?.includes(id)
              ? filters[FilterKeys.Subject]?.filter((ct) => ct !== id)
              : [...(filters[FilterKeys.Subject] || []), id],
          }));
        },
        onClearSubject: clearFilter(FilterKeys.Subject),
        onClearOnlyTutorGroups: clearFilter(FilterKeys.OnlyTutorGroups),
        onSelectOnlyTutorGroups: () =>
          setFilters((filters) => ({
            ...filters,
            [FilterKeys.OnlyTutorGroups]: ['1'],
            [FilterKeys.Subject]: undefined,
          })),
      };
    }, []);

  if (!schoolId) return null;
  const isOnlyTutorGroups = !!filters.only_tutor_groups?.length;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'groups-Filter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <FilterRow label={formatMessage({ id: 'section-Subjects' })}>
          <SubjectSelectMultiple
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={onClearSubject}
            selectedIds={filters.subject || []}
            onSelectSubjectId={onSelectSubject}
            placeholder={
              isOnlyTutorGroups
                ? formatMessage({ id: 'groups-GroupSubject-disabledFilter-Hint' })
                : undefined
            }
            disabled={isOnlyTutorGroups}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'groups-OnlyTutorGroups' })}>
          <Switch
            sx={{ ml: 1.25 }}
            onChange={(_, checked) => {
              checked ? onSelectOnlyTutorGroups() : onClearOnlyTutorGroups();
            }}
            checked={isOnlyTutorGroups}
          />
        </FilterRow>
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};
